import { z } from 'zod';
import { MIN_PASSWORD_LENGTH } from '../constants.js';
import { orgRoleSchema, platformRoleSchema, userPersonaSchema } from '../enums.js';
/**
 * Politique de mot de passe (docs/security.md §2). La vérification contre une liste de mots de
 * passe compromis est faite côté serveur uniquement : elle exige un appel réseau ou une base
 * locale, qu'on ne veut pas embarquer dans le bundle front.
 */
export const passwordSchema = z
    .string()
    .min(MIN_PASSWORD_LENGTH, `Le mot de passe doit contenir au moins ${MIN_PASSWORD_LENGTH} caractères`)
    .max(200, 'Le mot de passe ne peut pas dépasser 200 caractères')
    .refine((v) => /[a-z]/.test(v), 'Au moins une minuscule est requise')
    .refine((v) => /[A-Z]/.test(v), 'Au moins une majuscule est requise')
    .refine((v) => /[0-9]/.test(v), 'Au moins un chiffre est requis');
export const emailSchema = z
    .string()
    .trim()
    .toLowerCase()
    .email('Adresse e-mail invalide')
    .max(255);
export const registerSchema = z.object({
    email: emailSchema,
    password: passwordSchema,
    displayName: z.string().trim().min(2).max(100),
    /** Nom de l'organisation créée à l'inscription ; l'utilisateur en devient OWNER. */
    organizationName: z.string().trim().min(2).max(100),
});
/**
 * Mise à jour du profil par l'utilisateur lui-même (`PATCH /auth/me`). Seuls ses propres champs
 * d'identité sont modifiables ici ; le rôle et l'organisation relèvent de l'administration.
 */
export const updateProfileSchema = z
    .object({
    displayName: z.string().trim().min(2).max(100).optional(),
    email: emailSchema.optional(),
    /** Localisation approximative (ADR-0040, C6). `null` l'efface ; posées ensemble en pratique. */
    approxLat: z.number().min(-90).max(90).nullable().optional(),
    approxLon: z.number().min(-180).max(180).nullable().optional(),
})
    .refine((input) => input.displayName !== undefined ||
    input.email !== undefined ||
    input.approxLat !== undefined ||
    input.approxLon !== undefined, { message: 'Aucune modification fournie' });
/* ------------------------------------------------------------------ *
 * Onboarding : persona d'interface + état des visites guidées (ADR-0031).
 * ------------------------------------------------------------------ */
/** Identifiant de portée d'une visite (`overview`, `data`, `model`, `deploy`, …). Slug court. */
export const tourScopeSchema = z
    .string()
    .trim()
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'slug attendu (a-z, 0-9, tirets)')
    .max(40);
/** Trace d'une visite vue : version vue + horodatage + si elle a été passée plutôt que terminée. */
export const tourSeenSchema = z.object({
    version: z.number().int().min(1),
    seenAt: z.coerce.date(),
    skipped: z.boolean().default(false),
});
/**
 * État des visites vues, tel qu'exposé au front : dict `portée → trace`. Permissif et évolutif —
 * ajouter une visite n'impose aucun changement de contrat. Le front décide de (re)déclencher en
 * comparant la version stockée à la version courante de la visite.
 */
export const onboardingStateSchema = z.record(tourScopeSchema, tourSeenSchema);
/**
 * Écriture de l'onboarding par l'utilisateur lui-même (`PATCH /auth/me`, même surface que le
 * profil). Deux gestes indépendants, tous deux optionnels : choisir/mettre à jour son `persona`, et
 * **marquer une visite vue** (`markTourSeen`). Marquer une visite est **idempotent** : le service
 * fusionne la clé de portée dans `onboardingState` (il ne remplace jamais tout l'état).
 */
export const updateOnboardingSchema = z
    .object({
    persona: userPersonaSchema.optional(),
    markTourSeen: z
        .object({
        scope: tourScopeSchema,
        version: z.number().int().min(1),
        skipped: z.boolean().default(false),
    })
        .optional(),
})
    .refine((input) => input.persona !== undefined || input.markTourSeen !== undefined, {
    message: 'Aucune modification fournie',
});
export const loginSchema = z.object({
    email: emailSchema,
    password: z.string().min(1, 'Mot de passe requis'),
    /** Code TOTP à 6 chiffres, requis seulement si le second facteur est actif. */
    totpCode: z
        .string()
        .regex(/^\d{6}$/, 'Le code doit contenir 6 chiffres')
        .optional(),
});
/**
 * Réponse de connexion. Le jeton de rafraîchissement n'y figure pas : il est posé en cookie
 * HttpOnly par le serveur et reste hors de portée de JavaScript (PRD NFR-2).
 */
export const authTokensSchema = z.object({
    accessToken: z.string(),
    expiresIn: z.number().int().positive(),
    tokenType: z.literal('Bearer'),
});
/** Réponse partielle quand le second facteur est requis pour finaliser la connexion. */
export const totpRequiredSchema = z.object({
    totpRequired: z.literal(true),
    /** Jeton éphémère (5 min) prouvant que le mot de passe a déjà été validé. */
    challengeToken: z.string(),
});
export const loginResponseSchema = z.union([authTokensSchema, totpRequiredSchema]);
export const changePasswordSchema = z
    .object({
    currentPassword: z.string().min(1),
    newPassword: passwordSchema,
})
    .refine((d) => d.currentPassword !== d.newPassword, {
    message: 'Le nouveau mot de passe doit différer de l’ancien',
    path: ['newPassword'],
});
export const enableTotpSchema = z.object({
    /** Code produit par l'application d'authentification, prouvant que le secret est bien enrôlé. */
    code: z.string().regex(/^\d{6}$/),
});
export const totpSetupSchema = z.object({
    secret: z.string(),
    /** URI `otpauth://` à encoder en QR code. */
    otpauthUrl: z.string(),
});
export const inviteMemberSchema = z.object({
    email: emailSchema,
    role: orgRoleSchema,
});
export const acceptInviteSchema = z.object({
    token: z.string().min(1),
    password: passwordSchema,
    displayName: z.string().trim().min(2).max(100),
});
/** Identité de l'utilisateur courant, telle qu'exposée au front. */
export const currentUserSchema = z.object({
    id: z.string(),
    email: z.string(),
    displayName: z.string(),
    totpEnabled: z.boolean(),
    /** Rôle plateforme (ADR-0025). `USER` pour la quasi-totalité ; gate la console d'administration. */
    platformRole: platformRoleSchema,
    /** Persona d'interface (ADR-0031). `null` = jamais choisi → la coquille propose la modale. */
    persona: userPersonaSchema.nullable(),
    /** Localisation approximative déclarée (ADR-0040, C6) : repli de centrage de la carte. `null` si non renseignée. */
    approxLat: z.number().nullable(),
    approxLon: z.number().nullable(),
    /** Visites guidées déjà vues (ADR-0031), pour décider des déclenchements automatiques. */
    onboardingState: onboardingStateSchema,
    createdAt: z.coerce.date(),
    memberships: z.array(z.object({
        organizationId: z.string(),
        organizationName: z.string(),
        organizationSlug: z.string(),
        role: orgRoleSchema,
    })),
});
//# sourceMappingURL=auth.js.map